<template>
  <div
    ref="root"
    class="report-ms"
  >
    <button
      type="button"
      class="report-ms__control"
      :class="{ 'report-ms__control--open': open, 'report-ms__control--disabled': disabled }"
      :disabled="disabled"
      @click="toggle"
    >
      <span
        v-if="!modelValue.length"
        class="report-ms__placeholder"
      >
        {{ placeholder }}
      </span>
      <span
        v-else
        class="report-ms__tags"
      >
        <span
          v-for="v in modelValue"
          :key="v"
          class="report-ms__tag"
          @click.stop="remove(v)"
        >
          {{ v }}
          <span class="report-ms__tag-x">×</span>
        </span>
      </span>
      <span class="report-ms__chevron">▾</span>
    </button>
    <div
      v-if="open && !disabled"
      class="report-ms__menu"
    >
      <input
        v-model="query"
        class="report-ms__search"
        type="search"
        placeholder="Search…"
        @click.stop
      />
      <button
        type="button"
        class="report-ms__option report-ms__option--action"
        @click="clearAll"
      >
        Clear selection (All)
      </button>
      <button
        v-for="opt in filtered"
        :key="opt"
        type="button"
        class="report-ms__option"
        :class="{ 'report-ms__option--selected': modelValue.includes(opt) }"
        @click="toggleOption(opt)"
      >
        <span class="report-ms__check">
          {{ modelValue.includes(opt) ? '✓' : '' }}
        </span>
        {{ opt }}
      </button>
      <div
        v-if="!filtered.length"
        class="report-ms__empty"
      >
        No matches
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportTagMultiSelect',
  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      },
    },
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    placeholder: {
      type: String,
      default: '(All)',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
      query: '',
    };
  },
  computed: {
    filtered() {
      const q = (this.query || '').trim().toLowerCase();
      if (!q) return this.options;
      return this.options.filter((o) => String(o).toLowerCase().includes(q));
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) this.query = '';
    },
    onDocClick(e) {
      if (!this.$refs.root) return;
      if (!this.$refs.root.contains(e.target)) {
        this.open = false;
      }
    },
    toggleOption(opt) {
      const next = this.modelValue.includes(opt)
        ? this.modelValue.filter((v) => v !== opt)
        : [ ...this.modelValue, opt ];
      this.$emit('update:modelValue', next);
    },
    remove(opt) {
      if (this.disabled) return;
      this.$emit(
        'update:modelValue',
        this.modelValue.filter((v) => v !== opt),
      );
    },
    clearAll() {
      this.$emit('update:modelValue', []);
      this.open = false;
    },
  },
};
</script>

<style scoped>
.report-ms {
  position: relative;
  width: 100%;
}

.report-ms__control {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  min-height: 38px;
  padding: 4px 28px 4px 10px;
  border: none;
  border-radius: 0.5rem;
  background: #e0f2fe;
  text-align: left;
  cursor: pointer;
  position: relative;
}

.report-ms__control--open {
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.35);
}

.report-ms__control--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.report-ms__placeholder {
  color: #7c8194;
  font-size: 15px;
  padding-left: 2px;
}

.report-ms__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.report-ms__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #31333f;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #c7e4f7;
}

.report-ms__tag-x {
  color: #6b7280;
  font-size: 14px;
  line-height: 1;
}

.report-ms__chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #545c66;
  font-size: 12px;
  pointer-events: none;
}

.report-ms__menu {
  position: absolute;
  z-index: 40;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow: auto;
  background: #fff;
  border: 1px solid #c7e4f7;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 6px;
}

.report-ms__search {
  width: 100%;
  height: 34px;
  margin-bottom: 6px;
  padding: 0 0.6rem;
  border: none;
  border-radius: 0.4rem;
  background: #e0f2fe;
  font-size: 13px;
}

.report-ms__option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.4rem 0.5rem;
  border-radius: 0.35rem;
  font-size: 15px;
  color: #31333f;
  cursor: pointer;
}

.report-ms__option:hover,
.report-ms__option--selected {
  background: #e0f2fe;
}

.report-ms__option--action {
  color: #4b5563;
  font-size: 13px;
  border-bottom: 1px solid #eee;
  border-radius: 0;
  margin-bottom: 4px;
}

.report-ms__check {
  width: 1rem;
  color: #0066cc;
  font-weight: 700;
}

.report-ms__empty {
  padding: 0.5rem;
  color: #9aa3ad;
  font-size: 13px;
}
</style>
